import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiFilterSelect,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* -- Types ------------------------------------------------ */

interface YtdCase {
  id: number
  file_number: number | null
  open_date: string | null
  investigation_type: string | null
  case_name: string | null
  referral_customer_name: string | null
  referral_customer_id: number | null
  referral_contact_name: string | null
  referral_contact_id: number | null
  referral_contact_customer_id: number | null
  how_found: string | null
  estimated_billable: number
  time_minutes: number
  status: string
}

interface InvestigationType {
  id: number
  name: string
}

interface Summary {
  case_count: number
  total_estimated: number
  total_time_minutes: number
}

interface CaseHistoryYear {
  year: number
  case_count: number
  time_minutes: number
  estimated_billable: number
  avg_per_case: number
}

interface TypeBreakdown {
  type_id: number
  type_name: string
  case_count: number
  estimate: number
  count_pct: number
  dollar_pct: number
}

interface ByTypeYear {
  year: number
  total_cases: number
  total_estimate: number
  types: TypeBreakdown[]
}

interface CasesByType {
  investigation_types: { id: number; name: string }[]
  years: ByTypeYear[]
}

interface Props {
  cases: YtdCase[]
  year: number
  selected_type_id: number | null
  investigation_types: InvestigationType[]
  summary: Summary
  case_history: CaseHistoryYear[]
  cases_by_type: CasesByType
}

/* -- Helpers ---------------------------------------------- */

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

function formatCurrency(amount: number | null): string {
  if (!amount) return ""
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

function formatCurrencyShort(amount: number | null): string {
  if (!amount) return ""
  return "$" + Math.round(amount).toLocaleString("en-US")
}

function formatTimeHM(minutes: number): string {
  if (!minutes || minutes <= 0) return ""
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}:${m.toString().padStart(2, "0")}`
}

/* -- Columns ---------------------------------------------- */

const PER_PAGE = 25

function buildColumns(year: number): HpiColumn<YtdCase>[] {
  return [
    {
      header: "ID",
      width: "max-w-[60px]",
      render: (row) => (
        <a
          href={`/cases/${row.id}`}
          className="text-hpi-active-blue hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          {row.file_number && row.file_number > 0 ? row.file_number : ""}
        </a>
      ),
    },
    {
      header: "Date",
      width: "max-w-[100px]",
      render: (row) => (
        <a
          href={`/cases/${row.id}`}
          className="text-hpi-active-blue hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          {formatDate(row.open_date)}
        </a>
      ),
    },
    {
      header: "Type",
      width: "max-w-[150px]",
      render: (row) => (
        <div className="text-sm">{row.investigation_type ?? "-"}</div>
      ),
    },
    {
      header: "Case",
      render: (row) => (
        <a
          href={`/cases/${row.id}`}
          className="text-hpi-active-blue hover:underline"
          target="_blank"
          rel="noreferrer"
        >
          {row.case_name ?? "-"}
        </a>
      ),
    },
    {
      header: "Referred By",
      render: (row) => <ReferralCell row={row} year={year} />,
    },
    {
      header: "Estimated",
      subHeader: "Billable",
      width: "max-w-[110px]",
      render: (row) => (
        <div className="text-right text-sm">
          {row.estimated_billable > 0
            ? formatCurrency(row.estimated_billable)
            : ""}
        </div>
      ),
    },
    {
      header: "Time",
      width: "max-w-[80px]",
      render: (row) => (
        <div className="text-right text-sm">
          {formatTimeHM(row.time_minutes)}
        </div>
      ),
    },
    {
      header: "Status",
      width: "max-w-[100px]",
      render: (row) => <div className="text-right text-sm">{row.status}</div>,
    },
  ]
}

/* -- Referral Cell ---------------------------------------- */

function ReferralCell({ row, year }: { row: YtdCase; year: number }) {
  if (row.referral_customer_id && row.referral_customer_id > 0) {
    const refParams = new URLSearchParams({
      start_date: `${year}-01-01`,
      end_date: `${year}-12-31`,
    })
    return (
      <div className="text-sm">
        <div>
          <a
            href={`/customers/${row.referral_customer_id}/reports/referrals?${refParams}`}
            className="text-hpi-active-blue hover:underline"
            target="_blank"
            rel="noreferrer"
          >
            {row.referral_customer_name}
          </a>
        </div>
        {row.referral_contact_id && row.referral_contact_id > 0 && (
          <div className="text-hpi-med-gray text-xs">
            <a
              href={`/customers/${row.referral_contact_customer_id}/reports/referrals?${refParams}`}
              className="text-hpi-active-blue hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              {row.referral_contact_name}
            </a>
          </div>
        )}
      </div>
    )
  }

  if (row.how_found) {
    return <div className="text-sm">How found: {row.how_found}</div>
  }

  return <div className="text-hpi-med-gray text-sm">-</div>
}

/* -- Main Component --------------------------------------- */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Reports", href: "/reports/ytd_cases" },
  { title: "Cases by Year", href: "/reports/ytd_cases" },
]

export default function YtdCasesReport({
  cases,
  year,
  selected_type_id,
  investigation_types,
  summary,
  case_history,
  cases_by_type,
}: Props) {
  const [searchYear, setSearchYear] = useState(year.toString())
  const [typeId, setTypeId] = useState(selected_type_id?.toString() ?? "")
  const [page, setPage] = useState(1)

  const columns = buildColumns(year)
  const totalPages = Math.ceil(cases.length / PER_PAGE)
  const paginated = cases.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleUpdate = () => {
    const params: Record<string, string> = {}
    if (searchYear) params.year = searchYear
    if (typeId) params.type = typeId
    router.get("/reports/ytd_cases", params, { preserveState: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Cases by Year" />

      <div className="p-6">
        {/* Filter Bar */}
        <div className="bg-hpi-pale-gray mb-6 flex flex-wrap items-end gap-4 rounded-lg px-4 py-3">
          <div>
            <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
              Year
            </label>
            <input
              type="text"
              value={searchYear}
              onChange={(e) => setSearchYear(e.target.value)}
              maxLength={4}
              className="border-hpi-med-gray text-hpi-dark-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue h-8 w-16 rounded border bg-white px-2 text-sm focus:ring-1"
            />
          </div>
          <HpiFilterSelect
            label="Type"
            value={typeId}
            onChange={setTypeId}
            options={investigation_types.map((t) => ({
              value: t.id,
              label: t.name,
            }))}
            placeholder="All"
          />
          <button
            onClick={handleUpdate}
            className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-dark-blue h-8 rounded border px-5 text-sm font-medium transition-colors hover:text-white"
          >
            Search
          </button>
        </div>

        {/* Summary Stats */}
        <HpiStatsBar
          className="mb-6"
          stats={[
            {
              label: "Number of",
              subLabel: "Cases",
              value: summary.case_count,
            },
            {
              label: "Total Estimated",
              subLabel: "Billable",
              value: formatCurrency(summary.total_estimated),
            },
            {
              label: "Total Time",
              subLabel: "Worked",
              value: formatTimeHM(summary.total_time_minutes),
            },
          ]}
        />

        {/* Cases Table */}
        {cases.length > 0 ? (
          <HpiDataTable<YtdCase>
            title={`${year} Cases`}
            columns={columns}
            data={paginated}
            keyFn={(row) => row.id}
            pagination={
              cases.length > PER_PAGE ? (
                <HpiPagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={cases.length}
                  perPage={PER_PAGE}
                  onPageChange={setPage}
                />
              ) : undefined
            }
          />
        ) : (
          <div className="border-hpi-light-gray text-hpi-med-blue mt-4 rounded-lg border bg-white p-8 text-center">
            No cases found
          </div>
        )}

        {/* Case History Section */}
        {case_history && case_history.length > 0 && (
          <div className="border-hpi-light-gray mt-8 rounded-lg border bg-white">
            <div className="border-hpi-light-gray border-b px-4 py-3">
              <h3 className="text-hpi-dark-blue text-base font-semibold">
                Case History
              </h3>
              <p className="text-hpi-med-gray text-xs">
                Cases with no time/expense entries have been excluded
              </p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-hpi-light-gray bg-hpi-pale-gray border-b text-left">
                  <th className="px-4 py-2 font-medium">Year</th>
                  <th className="px-4 py-2 text-right font-medium"># Cases</th>
                  <th className="px-4 py-2 text-left font-medium">Time</th>
                  <th className="px-4 py-2 text-right font-medium">
                    Estimated Billable
                  </th>
                  <th className="px-4 py-2 text-right font-medium">
                    Avg $/Case
                  </th>
                </tr>
              </thead>
              <tbody>
                {case_history.map((row) => (
                  <tr
                    key={row.year}
                    className="border-hpi-light-gray hover:bg-hpi-pale-gray border-b last:border-b-0"
                  >
                    <td className="px-4 py-2">{row.year}</td>
                    <td className="px-4 py-2 text-right">{row.case_count}</td>
                    <td className="px-4 py-2">
                      {row.time_minutes > 0
                        ? formatTimeHM(row.time_minutes)
                        : ""}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {row.estimated_billable > 0
                        ? formatCurrency(row.estimated_billable)
                        : ""}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {row.avg_per_case > 0
                        ? formatCurrency(row.avg_per_case)
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Cases by Type Section */}
        {cases_by_type && cases_by_type.investigation_types.length > 0 && (
          <div className="border-hpi-light-gray mt-8 rounded-lg border bg-white">
            <div className="border-hpi-light-gray border-b px-4 py-3">
              <h3 className="text-hpi-dark-blue text-base font-semibold">
                Cases by Type{" "}
                <span className="text-hpi-active-blue text-xs font-normal">
                  #50% = 50% of the number of cases, $50% = 50% of total
                  estimated billable
                </span>
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-hpi-light-gray bg-hpi-pale-gray border-b text-left">
                    <th className="px-4 py-2 text-center font-medium">Year</th>
                    {cases_by_type.investigation_types.map((type) => (
                      <th
                        key={type.id}
                        className="px-3 py-2 text-right font-medium"
                      >
                        {type.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cases_by_type.years.map((row) => (
                    <tr
                      key={row.year}
                      className="border-hpi-light-gray hover:bg-hpi-pale-gray border-b last:border-b-0"
                    >
                      <td className="px-4 py-2 text-center font-medium">
                        {row.year}
                      </td>
                      {row.types.map((type) => (
                        <td key={type.type_id} className="px-3 py-2 text-right">
                          {type.estimate > 0 ? (
                            <>
                              <div>{formatCurrencyShort(type.estimate)}</div>
                              <div className="text-hpi-med-gray text-xs">
                                #{type.count_pct}% ${type.dollar_pct}%
                              </div>
                            </>
                          ) : null}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
